import type React from 'react'
import { Fragment } from 'react'
import styled from 'styled-components'

import LandlordPropertyReference from '@/components/Landlord/LandlordPropertyReference'
import type { Application, Address } from '@/types/Activity'
import type { LandlordReference } from '@/types/Landlord'
import { parseDate, toDateLabel } from '@/utils/date'

interface Props {
  application: Application
}

const Container = styled.div`
  #address-title {
    text-decoration: underline;
  }
`

const ContactDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-gap: 10px;
  align-items: center;
`

const LandlordReferencePanel: React.FC<Props> = ({ application }) => {
  const reference = application.reference as LandlordReference

  function getAddressString(address: Address) {
    const { name, line1, city, postcode } = address
    return [name, line1, city, postcode].filter(addressPart => addressPart).join(', ')
  }

  if (!reference || !reference.properties)
    return (
      <Container>
        <p>Error loading landlord reference</p>
      </Container>
    )

  const completedDate = application.milestones.find(
    milestone => milestone.event === 'subject.complete',
  )

  return (
    <Container>
      <ContactDetails>
        <div>
          <h4>Name</h4>
          <p>{application.name}</p>
        </div>
        <div>
          <h4>Email</h4>
          <p>{application.context?.email}</p>
        </div>
        <div>
          <h4>Phone</h4>
          <p>{application.context?.mobile}</p>
        </div>
      </ContactDetails>
      <p>
        {application.name} is the Landlord/Estate Agent for {reference.properties.length}{' '}
        propertie(s) on this application.
      </p>
      {reference.properties.map((property, index) => (
        <Fragment key={index}>
          <h3 id="address-title">{getAddressString(property.address)}</h3>
          <LandlordPropertyReference
            application={application}
            reference={property}
            address={property.address}
          />
          {index % 2 !== 1 && <hr />}
        </Fragment>
      ))}
      <div>
        <h4>Reference submitted</h4>
        <p>
          {completedDate
            ? toDateLabel({
                date: parseDate(completedDate.createdAt),
                dateFormat: 'dd/MM/yyyy HH:mm:ss',
              })
            : 'Not submitted'}
        </p>
      </div>
    </Container>
  )
}

export default LandlordReferencePanel
